// Shared formatting for submission timestamps.
//
// The API stores the moment of submission in UTC and, for newer submissions,
// the IANA timezone of the submitter's device (e.g. 'America/New_York'). We
// render the instant in that captured zone with a short zone code (EDT, PST…)
// so a submission always reads in the submitter's local wall-clock time and is
// unambiguous no matter where in the country the viewer is. Older submissions
// have no captured zone; those fall back to the viewer's local zone, still
// labeled with a code.

// The IANA timezone of the current device, to send when creating a submission.
export const getDeviceTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
};

const FORMAT_OPTIONS = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZoneName: 'short',
};

export const formatSubmittedAt = (submittedAt, submittedTz) => {
  if (!submittedAt) return '';
  const date = new Date(submittedAt);
  if (Number.isNaN(date.getTime())) return '';

  if (submittedTz) {
    try {
      return date.toLocaleString(undefined, { ...FORMAT_OPTIONS, timeZone: submittedTz });
    } catch {
      // Unknown/invalid zone name — fall through to the viewer's local zone.
    }
  }
  return date.toLocaleString(undefined, FORMAT_OPTIONS);
};

const DAY_OPTIONS = { year: 'numeric', month: '2-digit', day: '2-digit' };

// The calendar day a submission was made, as 'YYYY-MM-DD', in the same zone
// formatSubmittedAt reads it in: a printed date says the day the submitter's
// clock said, not the day it already was in UTC.
export const submittedDay = (submittedAt, submittedTz) => {
  if (!submittedAt) return '';
  const date = new Date(submittedAt);
  if (Number.isNaN(date.getTime())) return '';

  const dayIn = (timeZone) => {
    const parts = new Intl.DateTimeFormat('en-US', { ...DAY_OPTIONS, timeZone }).formatToParts(date);
    const part = (type) => parts.find((entry) => entry.type === type).value;
    return `${part('year')}-${part('month')}-${part('day')}`;
  };
  if (submittedTz) {
    try {
      return dayIn(submittedTz);
    } catch {
      // Unknown/invalid zone name — fall through to the viewer's local zone.
    }
  }
  return dayIn(undefined);
};
